import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  Spinner,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  toaster,
} from '@optiaxiom/react';
import { IconPlus } from '@optimizely/axiom-icons';

import { EmptyPage } from '@frontend/components';
import { AuthProvider, ToolsManagementPage } from '@frontend/types';

import { useGetAuthProviders } from '../../hooks';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: FIXME: OPAL-2388 - Missing types file
import type { ScopeBundle } from '../../types';

import { AuthProviderRow } from './AuthProviderRow';
import { ScopeBundleModal } from './ScopeBundleModal';

const OPTIID_IDENTIFIERS = ['optiid', 'opti_id', 'opti-id'];

const isOptiIdProvider = (provider: AuthProvider): boolean => {
  const lowerName = provider.name.toLowerCase();
  const lowerAuthType = provider.auth_type.toLowerCase();
  return OPTIID_IDENTIFIERS.some(id => lowerName.includes(id)) || lowerAuthType === 'optiid_sso';
};

type AuthProvidersTabProps = {
  setActivePage: (page: ToolsManagementPage) => void;
  onEditProvider: (provider: AuthProvider) => void;
};

export function AuthProvidersTab({ setActivePage, onEditProvider }: AuthProvidersTabProps) {
  const { providers, isPending, error } = useGetAuthProviders();
  const [expandedProviderId, setExpandedProviderId] = useState<string | null>(null);

  // Filter out OptiID providers - they are internal SSO and shouldn't be managed here
  const filteredProviders = useMemo(
    () => providers.filter(provider => !isOptiIdProvider(provider)),
    [providers]
  );
  const [scopeBundleModal, setScopeBundleModal] = useState<{
    open: boolean;
    providerId?: string;
    providerName?: string;
    bundle?: ScopeBundle;
  }>({ open: false });

  useEffect(() => {
    if (error) {
      toaster.create(error.message || 'Failed to load auth providers', { type: 'danger' });
    }
  }, [error]);

  const handleToggleExpand = (providerId: string) => {
    setExpandedProviderId(prev => (prev === providerId ? null : providerId));
  };

  const handleAddBundle = (providerId: string, providerName: string) => {
    setScopeBundleModal({ open: true, providerId, providerName });
  };

  const handleEditBundle = (providerId: string, providerName: string, bundle: ScopeBundle) => {
    setScopeBundleModal({ open: true, providerId, providerName, bundle });
  };

  const handleCloseModal = () => {
    setScopeBundleModal({ open: false });
  };

  if (isPending) {
    return (
      <Flex justifyContent="center" p="24">
        <Spinner />
      </Flex>
    );
  }

  if (filteredProviders.length === 0) {
    return (
      <Flex flexDirection="column" gap="16">
        <Flex flexDirection="row" justifyContent="flex-end">
          <Button appearance="primary" onClick={() => setActivePage('auth-provider-form')}>
            <IconPlus size={16} />
            Add Provider
          </Button>
        </Flex>
        <EmptyPage
          title="No auth providers found."
          description="Add an authentication provider to enable OAuth integrations."
        />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap="16">
      <Flex flexDirection="row" justifyContent="flex-end">
        <Button appearance="primary" onClick={() => setActivePage('auth-provider-form')}>
          <IconPlus size={16} />
          Add Provider
        </Button>
      </Flex>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell w="32" />
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>Auth Type</TableHeaderCell>
            <TableHeaderCell>Bundles</TableHeaderCell>
            <TableHeaderCell>Created</TableHeaderCell>
            <TableHeaderCell w="80">Actions</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredProviders.map(provider => (
            <AuthProviderRow
              key={provider.id}
              provider={provider}
              isExpanded={expandedProviderId === provider.id}
              onToggleExpand={() => handleToggleExpand(provider.id)}
              onAddBundle={() => handleAddBundle(provider.id, provider.name)}
              onEditBundle={bundleId => handleEditBundle(provider.id, provider.name, bundleId)}
              onEditProvider={() => onEditProvider(provider)}
            />
          ))}
        </TableBody>
      </Table>

      <ScopeBundleModal
        open={scopeBundleModal.open}
        onClose={handleCloseModal}
        providerId={scopeBundleModal.providerId}
        providerName={scopeBundleModal.providerName}
        bundle={scopeBundleModal.bundle}
      />
    </Flex>
  );
}
